import React, { useMemo } from "react";
import { TrendingUp } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import type { DataPoint } from "../../types/dashboard.types";

const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });

interface TrendPoint { date: string; calls: number; website: number }

const buildSeries = (series: Record<string, DataPoint[]>): TrendPoint[] => {
  const CALL_CLICKS:    DataPoint[] = series.CALL_CLICKS    ?? [];
  const WEBSITE_CLICKS: DataPoint[] = series.WEBSITE_CLICKS ?? [];
  const map: Record<string, TrendPoint> = {};
  [...CALL_CLICKS, ...WEBSITE_CLICKS].forEach((p: DataPoint) => {
    if (!map[p.date]) map[p.date] = { date: p.date, calls: 0, website: 0 };
  });
  CALL_CLICKS.forEach((p: DataPoint)    => { map[p.date].calls   += p.value; });
  WEBSITE_CLICKS.forEach((p: DataPoint) => { map[p.date].website += p.value; });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
};

interface Props {
  series: Record<string, DataPoint[]>;
  compareSeries?: Record<string, DataPoint[]>;
  compareEnabled?: boolean;
  primaryLabel?: string;
  compareLabel?: string;
}

const TrendChart: React.FC<Props> = ({
  series, compareSeries, compareEnabled,
  primaryLabel = "Période principale",
  compareLabel = "Période comparaison",
}) => {
  const primary = useMemo(() => buildSeries(series), [series]);
  const compare = useMemo(() => compareSeries ? buildSeries(compareSeries) : [], [compareSeries]);

   const merged = useMemo(() => {
    const len = Math.max(primary.length, compare.length);
    return Array.from({ length: len }, (_, i) => ({
      idx:        i,
      calls:      primary[i]?.calls   ?? 0,
      website:    primary[i]?.website ?? 0,
      callsCmp:   compare[i]?.calls   ?? 0,
      webCmp:     compare[i]?.website ?? 0,
      date:       primary[i]?.date ?? compare[i]?.date ?? "",
    }));
  }, [primary, compare]);

  if (!primary.length) return (
    <div className="h-[200px] flex items-center justify-center text-slate-300 text-sm">Aucune donnée</div>
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 lg:col-span-2">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[13px] font-bold text-slate-700">Évolution des actions</h3>
          <p className="text-[11px] text-slate-400">Appels + clics site web</p>
        </div>
        <TrendingUp size={16} className="text-blue-400" />
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={merged} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id="gC"  x1="0" y1="0" x2="0" y2="1"><stop offset="5%"  stopColor="#0F52BA" stopOpacity={0.18} /><stop offset="95%" stopColor="#0F52BA" stopOpacity={0} /></linearGradient>
            <linearGradient id="gW"  x1="0" y1="0" x2="0" y2="1"><stop offset="5%"  stopColor="#EA7A1E" stopOpacity={0.15} /><stop offset="95%" stopColor="#EA7A1E" stopOpacity={0} /></linearGradient>
            <linearGradient id="gCC" x1="0" y1="0" x2="0" y2="1"><stop offset="5%"  stopColor="#6366f1" stopOpacity={0.15} /><stop offset="95%" stopColor="#6366f1" stopOpacity={0} /></linearGradient>
            <linearGradient id="gWC" x1="0" y1="0" x2="0" y2="1"><stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.12} /><stop offset="95%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={fmtDate} interval={Math.floor(merged.length / 6)} />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
      <Tooltip
          contentStyle={{
            fontSize: 11,
            borderRadius: 10,
            border: "1px solid #e2e8f0",
          }}
          labelFormatter={(label) => fmtDate(String(label))}
        />
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
          <Area type="monotone" dataKey="calls"    name={`Appels (${primaryLabel})`}    stroke="#0F52BA" strokeWidth={2} fill="url(#gC)"  dot={false} />
          <Area type="monotone" dataKey="website"  name={`Web (${primaryLabel})`}       stroke="#EA7A1E" strokeWidth={2} fill="url(#gW)"  dot={false} />
          {compareEnabled && <>
            <Area type="monotone" dataKey="callsCmp" name={`Appels (${compareLabel})`}  stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 2" fill="url(#gCC)" dot={false} />
            <Area type="monotone" dataKey="webCmp"   name={`Web (${compareLabel})`}     stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 2" fill="url(#gWC)" dot={false} />
          </>}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TrendChart;